import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TelemetryService {
  private initialized = false;

  init(): void {
    const dsn = environment.sentryDsn;
    if (!dsn || this.initialized) {
      return;
    }

    try {
      const currentEnv = (environment as any).sentryEnvironment || (environment.production ? 'production' : 'development');
      Sentry.init({
        dsn,
        environment: currentEnv,
        release: environment.apiVersion || 'v1',
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
          Sentry.captureConsoleIntegration({ levels: ['warn', 'error'] })
        ],
        tracesSampleRate: 1.0,
        tracePropagationTargets: ['localhost', 'localhost:8080', /^\/api/],
        replaysSessionSampleRate: currentEnv === 'production' ? 0.1 : 1.0,
        replaysOnErrorSampleRate: 1.0,
      });
      this.initialized = true;
      console.info('[Telemetry] Sentry observability initialized.');
    } catch (err) {
      console.warn('[Telemetry] Could not initialize Sentry:', err);
    }
  }

  identify(identifier: string, traits?: Record<string, any>): void {
    if (!this.initialized) return;
    try {
      Sentry.setUser({
        email: identifier.includes('@') ? identifier : undefined,
        username: traits?.['username'] || identifier,
        id: traits?.['id'] ? String(traits['id']) : undefined,
        ...traits
      });
    } catch (err) {
      console.warn('[Telemetry] Error setting Sentry user:', err);
    }
  }

  clearUser(): void {
    if (!this.initialized) return;
    try {
      Sentry.setUser(null);
    } catch (err) {
      console.warn('[Telemetry] Error clearing Sentry user:', err);
    }
  }

  recordError(error: unknown, context?: Record<string, any>): void {
    if (!this.initialized) {
      return;
    }

    try {
      if (context) {
        Sentry.withScope((scope) => {
          scope.setExtras(context);
          Sentry.captureException(error);
        });
      } else {
        Sentry.captureException(error);
      }
    } catch (err) {
      console.warn('[Telemetry] Error forwarding exception to Sentry:', err);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
